import { InjectionToken } from '@angular/core';

import { IGoogleMap } from '../lib/_interfaces/google-map.interface';
import { ImageRatioEnum } from '../lib/_enums/image-ratio.enum';
import { AnimationsEnum } from '../lib/_enums/animations.enum';
import { googleMapData } from '../lib/const/google-map.config';
import { PositionConfig, PositionEnum } from '../lib/_enums/position.enum';
import { ThemesEnum } from '../lib/_enums/themes.enum';
import { MarginEnum } from '../lib/_enums/margin.enum';
import { BrandsEnum } from '../lib/_enums/brands.enum';
import { SizesEnum } from '../lib/_enums/sizes.enum';

export const SERVER_DEVICE_TYPE = new InjectionToken<boolean>('serverDeviceType');

const SHARED_POSITON_CONFIG: PositionConfig = {
  top: {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom'
  },
  'top-left': {
    originX: 'center',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'bottom'
  },
  'top-right': {
    originX: 'center',
    originY: 'top',
    overlayX: 'end',
    overlayY: 'bottom'
  },
  bottom: {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top'
  },
  'bottom-right': {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'end',
    overlayY: 'top'
  },
  'bottom-left': {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top'
  },
  right: {
    originX: 'end',
    originY: 'center',
    overlayX: 'start',
    overlayY: 'center'
  },
  left: {
    originX: 'start',
    originY: 'center',
    overlayX: 'end',
    overlayY: 'center'
  }
};

export interface SdLibSettings {
  wishList: boolean;
  animation: AnimationsEnum;
  productImageHasBorder: boolean;
  productBenefitHasBorder: boolean;
  productImageRatio: ImageRatioEnum;
  headerLayout: PositionEnum.left | PositionEnum.center;
  seoMultipleColumnLayout: boolean;
  breadcrumbMobileBackHome: boolean;
  googleMap: IGoogleMap;
  largerSubcategories: boolean;
  linkCardSubcategories: boolean;
  loaderTheme: ThemesEnum.light | ThemesEnum.dark;
  titleSizeConfig: SizesEnum.xl | SizesEnum.l | SizesEnum.m;
  componentMargin: MarginEnum.default_margin | MarginEnum.without_margin;
  showMoreDesktop: boolean;
  showPushBorder: boolean;
  tooltipPosition: PositionConfig;
}

const brandSettings: Record<BrandsEnum, SdLibSettings> = {
  [BrandsEnum.moulinex]: {
    wishList: false,
    animation: AnimationsEnum.fade,
    productImageHasBorder: true,
    productBenefitHasBorder: false,
    productImageRatio: ImageRatioEnum.r4_3,
    headerLayout: PositionEnum.left,
    breadcrumbMobileBackHome: false,
    googleMap: googleMapData,
    seoMultipleColumnLayout: false,
    loaderTheme: ThemesEnum.light,
    largerSubcategories: true,
    titleSizeConfig: SizesEnum.m,
    componentMargin: MarginEnum.default_margin,
    showMoreDesktop: true,
    linkCardSubcategories: false,
    showPushBorder: true,
    tooltipPosition: SHARED_POSITON_CONFIG
  },
  [BrandsEnum.tefal]: {
    wishList: false,
    animation: AnimationsEnum.fade,
    productImageHasBorder: true,
    productBenefitHasBorder: false,
    productImageRatio: ImageRatioEnum.r4_3,
    headerLayout: PositionEnum.left,
    breadcrumbMobileBackHome: false,
    googleMap: googleMapData,
    seoMultipleColumnLayout: false,
    loaderTheme: ThemesEnum.light,
    largerSubcategories: true,
    titleSizeConfig: SizesEnum.m,
    componentMargin: MarginEnum.default_margin,
    showMoreDesktop: true,
    linkCardSubcategories: true,
    showPushBorder: true,
    tooltipPosition: SHARED_POSITON_CONFIG
  },
  [BrandsEnum.krups]: {
    wishList: false,
    animation: AnimationsEnum.fade,
    productImageHasBorder: true,
    productImageRatio: ImageRatioEnum.r4_3,
    productBenefitHasBorder: false,
    headerLayout: PositionEnum.left,
    breadcrumbMobileBackHome: false,
    seoMultipleColumnLayout: false,
    googleMap: googleMapData,
    largerSubcategories: true,
    loaderTheme: ThemesEnum.light,
    titleSizeConfig: SizesEnum.l,
    componentMargin: MarginEnum.default_margin,
    showMoreDesktop: false,
    linkCardSubcategories: false,
    showPushBorder: false,
    tooltipPosition: SHARED_POSITON_CONFIG
  },
  [BrandsEnum.rowenta]: {
    wishList: false,
    animation: AnimationsEnum.slide,
    productImageHasBorder: true,
    productImageRatio: ImageRatioEnum.r4_3,
    productBenefitHasBorder: false,
    headerLayout: PositionEnum.left,
    breadcrumbMobileBackHome: false,
    seoMultipleColumnLayout: false,
    googleMap: googleMapData,
    largerSubcategories: true,
    loaderTheme: ThemesEnum.light,
    titleSizeConfig: SizesEnum.l,
    componentMargin: MarginEnum.default_margin,
    showMoreDesktop: false,
    linkCardSubcategories: true,
    showPushBorder: true,
    tooltipPosition: SHARED_POSITON_CONFIG
  },
  [BrandsEnum.calor]: {
    wishList: false,
    animation: AnimationsEnum.fade,
    productImageHasBorder: true,
    productBenefitHasBorder: false,
    productImageRatio: ImageRatioEnum.r4_3,
    headerLayout: PositionEnum.left,
    breadcrumbMobileBackHome: false,
    googleMap: googleMapData,
    seoMultipleColumnLayout: false,
    loaderTheme: ThemesEnum.light,
    largerSubcategories: true,
    titleSizeConfig: SizesEnum.m,
    componentMargin: MarginEnum.default_margin,
    showMoreDesktop: true,
    linkCardSubcategories: false,
    showPushBorder: true,
    tooltipPosition: SHARED_POSITON_CONFIG
  },
  [BrandsEnum.seb]: {
    wishList: false,
    animation: AnimationsEnum.fade,
    productImageHasBorder: true,
    productBenefitHasBorder: false,
    productImageRatio: ImageRatioEnum.r4_3,
    headerLayout: PositionEnum.left,
    breadcrumbMobileBackHome: false,
    googleMap: googleMapData,
    seoMultipleColumnLayout: false,
    loaderTheme: ThemesEnum.light,
    largerSubcategories: true,
    titleSizeConfig: SizesEnum.m,
    componentMargin: MarginEnum.default_margin,
    showMoreDesktop: true,
    linkCardSubcategories: false,
    showPushBorder: true,
    tooltipPosition: SHARED_POSITON_CONFIG
  },
  [BrandsEnum.hob]: undefined,
  [BrandsEnum.default]: undefined
};

export class SdLibConfig {
  private _currentBrand: BrandsEnum;

  constructor(brand: BrandsEnum) {
    this._currentBrand = brand;
  }

  get currentBrand(): BrandsEnum {
    return this._currentBrand;
  }

  get(brand?: BrandsEnum): SdLibSettings {
    return brandSettings[brand || this.currentBrand] ?? {} as SdLibSettings;
  }
}
